/**
 * @file ContextMenu.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-09
 * @modified 2026-04-20
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './ContextMenu.css';
import CustomIcon from './CustomIcon';

function ContextMenu({ menuItems, onNavigate }) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [deployed, setDeployed] = useState(false);
  const menuRef = useRef(null);

  const handleContextMenu = useCallback((e) => {
    // Custom menu only fires with the platform modifier held:
    // Cmd on Mac, Ctrl elsewhere. Plain right-click falls through to the browser's default.
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/i.test(navigator.platform);
    const modifierHeld = isMac ? e.metaKey : e.ctrlKey;
    if (!modifierHeld) {
      // If our menu was already open, close it so it doesn't sit on top of the browser menu
      setDeployed(false);
      setTimeout(() => setVisible(false), 400);
      return;
    }
    e.preventDefault();

    // Position the menu at cursor
    setPosition({ x: e.clientX, y: e.clientY });
    setVisible(true);
    // Trigger deploy on next frame so the transition plays
    requestAnimationFrame(() => setDeployed(true));
  }, []);

  const handleClose = useCallback(() => {
    setDeployed(false);
    // Wait for fade-out transition before unmounting
    setTimeout(() => setVisible(false), 400);
  }, []);

  const handleItemClick = useCallback((item) => {
    handleClose();
    // Navigate to first submenu item, or the item itself
    const firstSub = item.subItems?.find(sub => !sub.isHeader);
    onNavigate(firstSub ? firstSub.id : item.id);
  }, [handleClose, onNavigate]);

  // Close when cursor moves beyond the full orbit radius + padding
  const handleMouseMove = useCallback((e) => {
    if (!deployed) return;
    const dx = e.clientX - position.x;
    const dy = e.clientY - position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    // radius (60) + icon size (26) + hover grow (6) + padding (20)
    if (dist > 112) handleClose();
  }, [deployed, position, handleClose]);

  useEffect(() => {
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('click', handleClose);
    document.addEventListener('scroll', handleClose, true);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('click', handleClose);
      document.removeEventListener('scroll', handleClose, true);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleContextMenu, handleClose, handleMouseMove]);

  if (!visible) return null;

  // Filter out admin-only items already handled by menuItems array from Dashboard
  const items = menuItems;
  const count = items.length;
  const angleStep = 360 / count;

  return (
    <div
      ref={menuRef}
      className={`context-menu-overlay${deployed ? ' deployed' : ''}`}
      style={{ '--cx': `${position.x}px`, '--cy': `${position.y}px` }}
    >
      <div
        className="context-menu"
        style={{ left: position.x, top: position.y }}
      >
        <button
          className="menu-button material-symbols-rounded"
          onClick={(e) => { e.stopPropagation(); handleClose(); }}
        >
          apps
        </button>

        <div className="menu-items">
          {items.map((item, i) => {
            const isMaterialIcon = !item.isSvg && !item.isLetter;
            const tooltipText = item.parentLabel ? `${item.parentLabel} › ${item.label}` : item.label;
            return (
              <span
                key={item.id}
                className={`tooltip${isMaterialIcon ? ' material-symbols-rounded' : ''}${item.isLetter ? ' letter-icon' : ''}${item.isAdmin ? ' admin-item' : ''}`}
                style={{ '--i': i, '--angle': `${i * angleStep}deg` }}
                title={tooltipText}
                onClick={(e) => { e.stopPropagation(); handleItemClick(item); }}
              >
                {item.isSvg ? (
                  <CustomIcon name={item.icon} size={26} />
                ) : (
                  item.icon
                )}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ContextMenu;